import React, { useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import "./DashboardLayout.css";
import Logo from "src/components/logo";
import { useAuthContext } from "src/auth/useAuthContext";
import getDiscordLoginLink from "src/utils/generateDiscordLink";

// Replace this with your real Discord server invite URL
const DISCORD_SERVER_URL = "https://discord.gg/QTN3P5AxJb";

function getUserGroups(user) {
  if (!user) return [];

  // Try several common places Cognito groups can live
  return (
    user["cognito:groups"] ||
    user?.attributes?.["cognito:groups"] ||
    user?.signInUserSession?.idToken?.payload?.["cognito:groups"] ||
    []
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const rawEmail = user?.email || user?.attributes?.email;
  const emailLocalPart = rawEmail ? rawEmail.split("@")[0] : null;

  const displayName =
    user?.username ||
    user?.preferred_username ||
    user?.attributes?.preferred_username ||
    user?.name ||
    user?.given_name ||
    emailLocalPart ||
    rawEmail ||
    "User";

  const userGroups = getUserGroups(user);
  const isAdmin = Array.isArray(userGroups) && (userGroups.includes("admin") || userGroups.includes("provider"));

  const discordConnected = Boolean(user?.discordId);

  const handleLogout = () => {
    logout?.();
    setMenuOpen(false);
  };

  const handleAccountSettings = () => {
    navigate("/dashboard/user/account");
    setMenuOpen(false);
  };

  const handleMyEvents = () => {
    navigate("/dashboard/myevents");
    setMenuOpen(false);
  };

  const handleAdmin = () => {
    navigate("/dashboard/admin");
    setMenuOpen(false);
  };


  return (
    <>
      <div className="page-background" />

      <header className="header">
        <div className="logo">
          <Logo
            whiteLogo
            sx={{
              width: 150,
              height: "auto",
              display: "block",
              margin: 0,
              alignSelf: "center",
            }}
          />
        </div>

        <nav className="nav-center">
          <Link to="/">Home</Link>
          <Link to="/dashboard/games">Games</Link>
          <Link to="/dashboard/events">Events</Link>
          <Link to="/dashboard/calendar">Calendar</Link>

          {user && (
            <a
              href={
                discordConnected
                  ? DISCORD_SERVER_URL
                  : getDiscordLoginLink()
              }
              target={discordConnected ? "_blank" : undefined}
              rel={discordConnected ? "noopener noreferrer" : undefined}
            >
              Discord
            </a>
          )}
          <Link to="/terms-and-conditions">Terms of Use</Link>
        </nav>

        <div className="nav-right">
          {user ? (
            <div className="user-menu">
              <button
                type="button"
                className="user-menu-trigger"
                onClick={() => setMenuOpen((o) => !o)}
              >
                <span className="user-menu-label">Signed in as</span>
                <span className="user-menu-name">{displayName}</span>
                <span className="user-menu-caret">▾</span>
              </button>

              <div
                className={`user-menu-dropdown ${
                  menuOpen ? "open" : ""
                }`}
              >
                {/* My Events */}
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={handleMyEvents}
                >
                  My Events
                </button>

                {/* Account Settings */}
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={handleAccountSettings}
                >
                  Account Settings
                </button>

                {/* Admin Panel (only if actually in admin group) */}
                {isAdmin && (
                  <button
                    type="button"
                    className="user-menu-item"
                    onClick={handleAdmin}
                  >
                    Admin Panel
                  </button>
                )}

                {/* Divider */}
                <div className="user-menu-divider" />

                {/* Logout */}
                <button
                  type="button"
                  className="user-menu-item user-menu-logout"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            </div>
          ) : (
            <Link to="/login" className="button">
              Log in
            </Link>
          )}
        </div>
      </header>

      <main>
        <Outlet />
      </main>
    </>
  );
}
